import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import axios from '../../services/api';
import { useI18n } from '../../contexts/I18nContext';
import { generateDownloadFilename } from '../../utils/fileUtils';
import { API_BASE_URL } from '../../lib/constants';
import './Showcase.scss';

const Showcase = () => {
  const { t } = useI18n();
  const [showcaseItems, setShowcaseItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const gridRef = useRef(null);
  const [loadedMap, setLoadedMap] = useState({});
  const recalcMasonry = () => {
    const grid = gridRef.current;
    if (!grid) return;

    // 如果只有一个元素且处于 flex 居中模式，不需要计算 span
    if (grid.children.length <= 1) {
      const cards = grid.querySelectorAll('.showcase-item');
      cards.forEach(card => {
        card.style.gridRowEnd = 'auto';
      });
      return;
    }

    const style = window.getComputedStyle(grid);
    const rowHeight = parseInt(style.getPropertyValue('grid-auto-rows')) || 8;
    const gapY = parseInt(style.getPropertyValue('row-gap')) || parseInt(style.getPropertyValue('grid-row-gap')) || parseInt(style.getPropertyValue('gap')) || 0;
    const cards = grid.querySelectorAll('.showcase-item');
    cards.forEach(card => {
      const img = card.querySelector('img');
      if (!img) return;
      const w = img.clientWidth || img.offsetWidth || 0;
      const nh = img.naturalHeight || 0;
      const nw = img.naturalWidth || 1;
      const contentH = nw > 0 && w > 0 ? Math.round(w * (nh / nw)) : img.offsetHeight;
      const h = contentH + 2;
      const span = Math.max(1, Math.ceil((h + gapY) / (rowHeight + gapY)));
      card.style.gridRowEnd = `span ${span}`;
    });
  };

  const handleImageLoad = (id) => {
    setLoadedMap((prev) => ({ ...prev, [id]: true }));
    recalcMasonry();
  };

  // 获取展示图片数据
  const fetchShowcaseImages = async (page = 0, append = false) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });

      const response = await axios.get(`/showcase/images?${params}`);
      const data = response.data;
      if (append) {
        setShowcaseItems(prev => [...prev, ...data.images]);
      } else {
        setShowcaseItems(data.images);
      }
      
      setHasMore(page < data.totalPages - 1);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Get showcase images failed:', error);
      // toast.error(t('showcase.loadError'));
      
      // 不再显示占位数据，完全依赖API
      if (!append) {
        setShowcaseItems([]);
      }
    }
  };

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      await fetchShowcaseImages(0);
      setLoading(false);
      recalcMasonry();
    };
    
    initData();
  }, []);

  useEffect(() => {
    const handler = () => recalcMasonry();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // 加载更多
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    await fetchShowcaseImages(currentPage + 1, true);
    setLoadingMore(false);
    recalcMasonry();
  };

  // 处理图片加载错误
  const handleImageError = (e) => {
    e.target.src = `${API_BASE_URL}/api/placeholder/300/200`;
    setTimeout(recalcMasonry, 50);
  };

  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  };

  const openMediaInNewTab = (item) => {
    const hasVideo = typeof item?.videoUrl === 'string' && item.videoUrl.trim().length > 0;
    const url = hasVideo ? getAbsoluteUrl(item.videoUrl) : getAbsoluteUrl(item.imageUrl);
    if (url) window.open(url, '_blank');
  };


  // 处理下载
  const handleDownload = async (item) => {
    const hasVideo = typeof item?.videoUrl === 'string' && item.videoUrl.trim().length > 0;
    const url = hasVideo ? getAbsoluteUrl(item.videoUrl) : getAbsoluteUrl(item.imageUrl);
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      
      let ext = hasVideo ? 'mp4' : 'png';
      if (!hasVideo) {
        const u = url || '';
        const m = u.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
        const e = ((m && m[1]) || '').toLowerCase();
        if (['jpg','jpeg','png','webp','gif'].includes(e)) ext = e === 'jpeg' ? 'jpg' : e;
      }
      
      const filename = generateDownloadFilename(item.generationId);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${filename}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      toast.success(t('showcase.downloadStarted'));
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  return (
    <div className="showcase-container">
      <header className="showcase-header">
        <h1>{t('showcase.title')}</h1>
        <p>{t('showcase.subtitle')}</p>
      </header>

      {/* 加载状态 */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      ) : (
        <>
          <div className="showcase-grid" ref={gridRef}>
            {showcaseItems.map(item => {
              const isLoaded = !!loadedMap[item.id];
              return (
                <div 
                  key={item.id} 
                  className={`showcase-item ${isLoaded ? 'is-loaded' : ''}`}
                >
                  <div className="image-container">
                    {!isLoaded && (<div className="img-skeleton" />)}
                    <img 
                      src={getAbsoluteUrl(item.imageUrl)} 
                      alt={item.title || t('showcase.generatedImage')} 
                      onError={handleImageError}
                      onClick={() => openMediaInNewTab(item)}
                      onLoad={() => handleImageLoad(item.id)}
                      style={{ opacity: isLoaded ? 1 : 0 }}
                    />
                    {(() => {
                      const hasVideo = typeof item?.videoUrl === 'string' && item.videoUrl.trim().length > 0;
                      return (
                        <div className={`media-overlay ${hasVideo ? 'is-visible' : ''}`}>
                          {hasVideo && (
                            <button className="overlay-btn overlay-play" onClick={(e) => { e.stopPropagation(); openMediaInNewTab(item); }} aria-label="Play">
                              <svg width="22" height="22" viewBox="0 0 24 24"><path d="M8 5l11 7-11 7z" fill="#ffffff"/></svg>
                            </button>
                          )}
                          <button className="overlay-btn overlay-download" onClick={(e) => { e.stopPropagation(); handleDownload(item); }} aria-label="Download">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 3v10" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M7 10l5 5 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 21h14" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 加载更多 */}
          {hasMore && (
            <div className="load-more">
              <button 
                className="btn-load-more" 
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? t('common.loading') : t('common.loadMore')}
              </button>
            </div>
          )}

          {/* 无更多数据提示 */}
          {!hasMore && showcaseItems.length > 0 && (
            <div className="no-more-data">
              <p>{t('showcase.noMoreImages')}</p>
            </div>
          )}

          {/* 空状态 */}
          {showcaseItems.length === 0 && !loading && (
            <div className="showcase_empty-state">
              <div className="empty-icon">🖼️</div>
              <h3>{t('showcase.noImagesFound')}</h3>
              <p>{t('showcase.noImagesDescription')}</p>
            </div>
          )}
        </>
      )}
      
    </div>
  );
};

export default Showcase;
